/**
 * A saved chemistry setup and the bench, each way — and the recording that
 * turns what a teacher does on the bench into a guided experiment's steps.
 *
 * Pure: no React, no fetching. See
 * docs/FeatureDesigns/lab-setups/lab-setups-target.md §6.4.
 */
import type { GuidedExpectation, GuidedProcedure, GuidedStep, labSetups as L } from '@padcanvas/shared';
import { findSubstance, formatAmount, perAddFor } from './substanceCatalog';
import { newContainer, type LabContainer } from './labState';
import { VESSEL_LABELS } from './vesselTypes';
import { defaultStandFor, isStandValidFor, standAllowsBurner, type StandType } from './standTypes';
import type { BurnerType } from './burnerTypes';
import { ROOM_C } from './temperature';
import type { BenchAttempt } from './guidedRun';

export type ChemistrySetupPayload = L.ChemistrySetupPayload;
export type ChemistrySetupVessel = L.ChemistrySetupVessel;

/** The payload version the bench writes — the adapter's. */
export const CHEMISTRY_SETUP_VERSION = 1;

/**
 * The chapters the chemistry lab is taught in, for the save dialog — by
 * canonical module id, with the curriculum's names (sciencexp-app's
 * curriculum.generated.ts). The chapters the guided catalogue is filed under.
 */
export const CHEMISTRY_CHAPTER_OPTIONS: readonly { id: string; label: string }[] = [
  { id: 'dd39021b-6e7d-4438-b9d4-0a353abbd97f', label: 'Chemical Reactions' },
  { id: '0bbeddab-5e81-4e45-9a99-da47d5cc09dd', label: 'Acids, Bases & Salts' },
  { id: '1fb95695-73f8-438e-83ae-5ea56d70be66', label: 'Metals and Non-metals' },
  { id: 'cc27050e-6656-4af8-9712-5d7d93c43949', label: 'Mole Concept' },
  { id: 'd71a049d-1040-4326-ba3c-fa1fd66d787b', label: 'Study of Compounds' },
];

const isGas = (id: string): boolean => findSubstance(id)?.category === 'gas';

/**
 * The bench as it is set out — what a setup saves. What happened on it is
 * left behind: outcomes, the notebook, swirls, the pH paper, and any gas that
 * came down a tube (opening the setup sends it again).
 */
export function benchSetup(containers: LabContainer[], labels: readonly (string | undefined)[] = []): ChemistrySetupVessel[] {
  return containers.map((c, i) => {
    const pipeTo = c.pipeToId ? containers.findIndex(x => x.id === c.pipeToId) : -1;
    const label = labels[i]?.trim();
    return {
      vesselType: c.vesselType,
      standType: c.standType,
      burnerType: c.burnerType,
      temperatureC: c.temperatureC,
      ...(label ? { label } : {}),
      contents: c.contents
        .filter(x => !isGas(x.substanceId))
        .map(x => ({ substanceId: x.substanceId, qty: x.qty, ...(x.concentrationM ? { concentrationM: x.concentrationM } : {}) })),
      ...(c.hasGlassRod && c.glassRodSubstanceId ? { rod: c.glassRodSubstanceId } : {}),
      ...(pipeTo >= 0 ? { pipeTo } : {}),
    };
  });
}

/**
 * A saved bench, ready to draw — before anything is worked out. A stand the
 * vessel cannot use is put right as a reshape would, and a burner where a
 * flame cannot go is taken away: the bench never opens on apparatus it would
 * not let a student build.
 */
export function containersFromSetup(vessels: readonly ChemistrySetupVessel[]): LabContainer[] {
  const made = vessels.map(v => {
    const c = newContainer(v.vesselType);
    const standType = isStandValidFor(v.vesselType, v.standType as StandType) ? v.standType as StandType : defaultStandFor(v.vesselType);
    const burnerType: BurnerType = standAllowsBurner(standType) ? v.burnerType : 'none';
    return {
      ...c,
      standType,
      burnerType,
      temperatureC: burnerType === 'none' ? ROOM_C : v.temperatureC,
      contents: v.contents.map(x => ({ ...x })),
      hasGlassRod: !!v.rod,
      glassRodSubstanceId: v.rod ?? null,
    };
  });
  return made.map((c, i) => {
    const to = vessels[i].pipeTo;
    return to !== undefined && made[to] ? { ...c, pipeToId: made[to].id } : c;
  });
}

/** A teacher's experiment as the guided runner plays one. */
export function procedureOf(payload: ChemistrySetupPayload, title: string): GuidedProcedure | undefined {
  if (!payload.steps?.length) return undefined;
  return {
    id: 'saved-setup',
    title,
    tagline: '',
    description: '',
    subject: 'chemistry',
    glyph: '⚗️',
    bench: payload.vessels.map(v => ({ vesselType: v.vesselType, label: v.label ?? VESSEL_LABELS[v.vesselType] })),
    steps: payload.steps,
    conclusion: payload.conclusion ?? '',
  };
}

/** Same arrangement, written the same way — for "has this changed since it was saved?". */
export function sameSetup(a: ChemistrySetupVessel[], b: ChemistrySetupVessel[]): boolean {
  const strip = (vs: ChemistrySetupVessel[]) => vs.map(({ label: _l, ...v }) => v);
  return JSON.stringify(strip(a)) === JSON.stringify(strip(b));
}

// ── Recording ──────────────────────────────────────────────────────────────

/**
 * One recorded step, with what the editor needs to keep writing its
 * instruction for it: whether the teacher has rewritten it (then it is theirs),
 * and how much of the reagent the vessel held before this step poured.
 */
export interface RecordedStep {
  step: GuidedStep;
  edited: boolean;
  heldBefore?: number;
}

/** How the steps name each vessel: its label, else "the beaker" — or
 *  "beaker 2" when there are two. */
export function vesselNames(vessels: readonly ChemistrySetupVessel[]): string[] {
  return vessels.map((v, i) => {
    if (v.label?.trim()) return v.label.trim();
    const kind = VESSEL_LABELS[v.vesselType].toLowerCase();
    const same = vessels.filter(x => x.vesselType === v.vesselType && !x.label?.trim());
    if (same.length < 2) return `the ${kind}`;
    return `${kind} ${vessels.slice(0, i + 1).filter(x => x.vesselType === v.vesselType && !x.label?.trim()).length}`;
  });
}

const reagent = (id: string): string => findSubstance(id)?.displayName ?? id;
const capital = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

/** The instruction a recorded step reads, until the teacher writes their own. */
export function defaultInstruction(rec: RecordedStep, names: readonly string[]): string {
  const { expect: e, container } = rec.step;
  const at = names[container] ?? 'the vessel';
  switch (e.kind) {
    case 'add-substance': {
      const s = findSubstance(e.substanceId);
      const amount = s && e.qty !== undefined ? `${formatAmount(s, e.qty - (rec.heldBefore ?? 0))} of ` : '';
      return `Add ${amount}${reagent(e.substanceId)} to ${at}.`;
    }
    case 'dip-test': return `Dip a strip of pH paper into ${at}.`;
    case 'dip-rod': return `Dip the glass rod in ${reagent(e.substanceId)} and rest it in ${at}.`;
    case 'attach-burner': return `Light a burner under ${at}.`;
    case 'heat': return e.toBand === 'boiling' ? `Heat ${at} until it boils.` : `Warm ${at} over the flame.`;
    case 'connect-pipe': return `Run a delivery tube from ${at} into ${names[e.toContainer] ?? 'the other vessel'}.`;
    case 'swirl': return `Swirl ${at} to mix it.`;
    case 'observe': return `Watch ${at}.`;
    case 'answer-quantity': return rec.step.instruction;
  }
}

/** Every unedited instruction rewritten for these names — after a vessel is renamed. */
export function renameSteps(steps: RecordedStep[], names: readonly string[]): RecordedStep[] {
  return steps.map(r => (r.edited ? r : { ...r, step: { ...r.step, instruction: capital(defaultInstruction(r, names)) } }));
}

const make = (expect: GuidedExpectation, container: number, names: readonly string[], heldBefore?: number): RecordedStep => {
  const rec: RecordedStep = { step: { instruction: '', container, expect }, edited: false, ...(heldBefore !== undefined ? { heldBefore } : {}) };
  return { ...rec, step: { ...rec.step, instruction: capital(defaultInstruction(rec, names)) } };
};

/**
 * What a recording makes of something the teacher just did — the steps so far
 * with it added, or `null` when it cannot be a step (the caller refuses it and
 * says `why`).
 *
 * A second pour of the same reagent into the same vessel, straight after the
 * first, is one step asking for the larger amount — which is how the runner
 * judges it (`judge` compares what the vessel holds), and how a person would
 * write it.
 */
export function recordAttempt(
  steps: RecordedStep[], attempt: BenchAttempt, names: readonly string[],
  vesselType: (container: number) => LabContainer['vesselType'],
): { steps: RecordedStep[] } | { why: string } {
  switch (attempt.kind) {
    case 'off-script':
      return { why: `While recording, the glassware stays as it was when you started — ${attempt.what} is not a step. Stop recording to change it.` };
    case 'rod-near-gas':
      return { why: 'Holding the rod near another vessel\'s gas cannot be a recorded step yet.' };
    case 'add-substance': {
      const last = steps[steps.length - 1];
      const s = findSubstance(attempt.substanceId);
      const helping = s ? perAddFor(s, vesselType(attempt.container)) : 0;
      if (last && last.step.container === attempt.container && last.step.expect.kind === 'add-substance'
        && last.step.expect.substanceId === attempt.substanceId) {
        const merged = { ...last, step: { ...last.step, expect: { ...last.step.expect, qty: attempt.qty } } };
        return { steps: [...steps.slice(0, -1), merged.edited ? merged : { ...merged, step: { ...merged.step, instruction: capital(defaultInstruction(merged, names)) } }] };
      }
      const heldBefore = Math.max(0, attempt.qty - helping);
      // One helping into a vessel that had none is what a step without an
      // amount asks for; anything else states the amount, so the runner holds
      // the student to it.
      const expect: GuidedExpectation = heldBefore === 0
        ? { kind: 'add-substance', substanceId: attempt.substanceId }
        : { kind: 'add-substance', substanceId: attempt.substanceId, qty: attempt.qty };
      return { steps: [...steps, make(expect, attempt.container, names, heldBefore || undefined)] };
    }
    case 'dip-test': return { steps: [...steps, make({ kind: 'dip-test' }, attempt.container, names)] };
    case 'dip-rod': return { steps: [...steps, make({ kind: 'dip-rod', substanceId: attempt.substanceId }, attempt.container, names)] };
    case 'attach-burner': return { steps: [...steps, make({ kind: 'attach-burner' }, attempt.container, names)] };
    case 'connect-pipe': return { steps: [...steps, make({ kind: 'connect-pipe', toContainer: attempt.toContainer }, attempt.container, names)] };
    case 'swirl': return { steps: [...steps, make({ kind: 'swirl' }, attempt.container, names)] };
  }
}

/**
 * The burner has settled in a hot band. Heating straight on from a heat step
 * in the same vessel is the same step, ending somewhere hotter — the slider
 * crosses "warm" on its way to boiling, and that is not two things to do.
 */
export function recordHeat(steps: RecordedStep[], container: number, band: 'heated' | 'boiling', names: readonly string[]): RecordedStep[] {
  const last = steps[steps.length - 1];
  const step = make({ kind: 'heat', toBand: band }, container, names);
  if (last && last.step.container === container && last.step.expect.kind === 'heat') {
    return [...steps.slice(0, -1), last.edited ? { ...last, step: { ...last.step, expect: { kind: 'heat', toBand: band } } } : step];
  }
  return [...steps, step];
}

/** A pause to watch, after step `after` (−1: before the first). */
export function insertWatch(steps: RecordedStep[], after: number, names: readonly string[]): RecordedStep[] {
  const container = steps[after]?.step.container ?? steps[0]?.step.container ?? 0;
  const next = [...steps];
  next.splice(after + 1, 0, make({ kind: 'observe' }, container, names));
  return next;
}

/** Saved steps, back in the editor. A step whose words are what the recorder
 *  would write is still the recorder's, and follows a renamed vessel. */
export function toRecorded(steps: readonly GuidedStep[], names: readonly string[]): RecordedStep[] {
  return steps.map(step => {
    const rec: RecordedStep = { step, edited: false };
    return { ...rec, edited: capital(defaultInstruction(rec, names)) !== step.instruction };
  });
}
